import { DataSourceState } from '../../../../types';

export function groupBySchema(dataSources: DataSourceState[]): Map<string, DataSourceState[]> {
    const map = new Map<string, DataSourceState[]>();
    for (const source of dataSources) {
        const group = map.get(source.schemaName);
        if (group) {
            group.push(source);
        } else {
            map.set(source.schemaName, [source]);
        }
    }
    return map;
}

export type IntersectedField = {
    commonFieldId: string;
    label: string;
    sourceId: number;
    fieldId: string;
};

/**
 * Computes the intersection of fields across data sources using `commonFieldId`.
 * Sources with zero fields are excluded so they don't zero out the result.
 * Returns fields ordered by the first non-empty source (the representative).
 */
export function getIntersectedFields(sources: DataSourceState[]): IntersectedField[] {
    const nonEmpty = sources.filter((s) => Object.keys(s.fieldsCfg).length > 0);
    if (nonEmpty.length === 0) {
        return [];
    }

    const sets = nonEmpty.map((s) => {
        const ids = new Set<string>();
        for (const cfg of Object.values(s.fieldsCfg)) {
            if (cfg.commonFieldId) {
                ids.add(cfg.commonFieldId);
            }
        }
        return ids;
    });

    let intersection = sets[0];
    for (let i = 1; i < sets.length; i++) {
        intersection = new Set([...intersection].filter((id) => sets[i].has(id)));
    }

    const representative = nonEmpty[0];
    const result: IntersectedField[] = [];
    for (const [fieldId, cfg] of Object.entries(representative.fieldsCfg)) {
        if (cfg.commonFieldId && intersection.has(cfg.commonFieldId)) {
            result.push({
                commonFieldId: cfg.commonFieldId,
                label: cfg.label,
                sourceId: representative.id,
                fieldId,
            });
        }
    }
    return result;
}

/**
 * Finds the fieldId in a source whose FieldConfig has the given commonFieldId.
 */
export function findFieldByCommonId(
    source: DataSourceState,
    commonFieldId: string,
): string | undefined {
    for (const [fieldId, cfg] of Object.entries(source.fieldsCfg)) {
        if (cfg.commonFieldId === commonFieldId) {
            return fieldId;
        }
    }
    return undefined;
}
